import { useMemo } from 'react'

import { EUserJourneyType } from '@pkg/lib-data'

import { cn } from '@wl/web-toolkit'

import { productCardRefClicked } from '../../lib/analytics'
import { getDomainWebsite } from '../../lib/data-fetching/ssr'
import type { ProductCardDataShows } from '../../lib/transforms/shows'
import { EMPTY_IMAGE_URLS } from '../../lib/transforms/utils'
import styles from './shows-card.module.scss'

interface ShowsCardProps {
  data: ProductCardDataShows
  categoryId: string
}

export const ShowsCard = (props: ShowsCardProps) => {
  const { data } = props

  const {
    title,
    dateLabelPrimary,
    dateLabelSecondary,
    href,
    marketingLabel,
    totalPriceFormatted,
    currencySymbol,
    imageUrls,
    resourceLevel,
    locationLabel,
    soldOut,
  } = data.computed

  const _imageUrls = useMemo(() => {
    return imageUrls && imageUrls.length > 0 ? imageUrls : [EMPTY_IMAGE_URLS[EUserJourneyType.SHOWS]]
  }, [imageUrls])

  const fallbackImageUrl = EMPTY_IMAGE_URLS[EUserJourneyType.SHOWS].imageUrl
  const ultimateFallback = ''

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Track product card click - same pattern as client
    if (typeof window !== 'undefined') {
      const screenName = window.location.pathname + window.location.search

      productCardRefClicked({
        productId: data.computed.id,
        name: title,
        categoryId: 'shows',
        price: data.computed.totalPrice,
        currencyIso: data.computed.currencyIso,
        date: dateLabelPrimary,
        screenName,
      })

      // Navigate after tracking
      window.location.href = href
    }
  }

  const resourceLevelLabelComputed = defineAvailableLabelPerShows(resourceLevel)

  const seatsLabel =
    resourceLevel > 0 && resourceLevel <= MAX_TICKETS_TO_SHOW_SEATS_LABEL && !soldOut
      ? resourceLevelLabelComputed
      : false

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const currentTarget = e.currentTarget

    // Prevent infinite loop: if we're already on the fallback or ultimate fallback, stop trying
    if (currentTarget.dataset.fallbackUsed === 'true' || currentTarget.dataset.ultimateFallbackUsed === 'true') {
      currentTarget.onerror = null

      return
    }

    // If current src is the fallback image and it failed, use ultimate fallback
    if (currentTarget.src === fallbackImageUrl || currentTarget.src.endsWith('empty-placeholder.svg')) {
      currentTarget.dataset.ultimateFallbackUsed = 'true'
      currentTarget.onerror = null
      currentTarget.src = ultimateFallback

      return
    }

    // First fallback attempt
    currentTarget.dataset.fallbackUsed = 'true'
    currentTarget.onerror = null
    currentTarget.src = fallbackImageUrl
  }

  const domain = getDomainWebsite()
  const mosqitoTickerUrl = `${domain}/assets/images/common/mosqito-ticker.svg`

  let seatsLabelElement: React.ReactNode

  if (soldOut) {
    seatsLabelElement = <div className={cn(styles['seats-label'], styles['seats-label--sold-out'])}>{T.soldOut}</div>
  } else if (seatsLabel) {
    seatsLabelElement = <div className={cn(styles['seats-label'], styles['seats-label-shows'])}>{seatsLabel}</div>
  } else {
    seatsLabelElement = <div />
  }

  return (
    <li className={styles.root}>
      {soldOut ? (
        <div className={styles.content}>
          <div className={styles['labels-container']}>
            {marketingLabel ? (
              <div className={styles['marketing-label']}>
                <img src={mosqitoTickerUrl} alt="" /> {marketingLabel}
              </div>
            ) : (
              <div />
            )}
            <div className={styles['labels-wrap']}>
              <div className={styles['title-block']}>
                <h3 className={styles.title}>{title}</h3>
                <div className={styles.date}>
                  <div>{dateLabelPrimary}</div>
                  <span> | </span>
                  <div>{dateLabelSecondary}</div>
                </div>
                {locationLabel && <div className={styles.date}>{locationLabel}</div>}
              </div>
              <div className={styles['price-block']}>
                {seatsLabelElement}
                <div className={styles['price-label']}>
                  <span className={styles.price}>{totalPriceFormatted}</span>
                  <span className={styles.currency}>{currencySymbol}</span>
                </div>
              </div>
            </div>
          </div>
          <img
            className={cn(styles.image, soldOut && styles['image--sold-out'])}
            src={_imageUrls[0]?.imageUrl}
            alt={_imageUrls[0]?.alt}
            onError={handleImageError}
          />
        </div>
      ) : (
        <a className={styles.content} href={href} onClick={handleCardClick}>
          <div className={styles['labels-container']}>
            {marketingLabel ? (
              <div className={styles['marketing-label']}>
                <img src={mosqitoTickerUrl} alt="" /> {marketingLabel}
              </div>
            ) : (
              <div />
            )}
            <div className={styles['labels-wrap']}>
              <div className={styles['title-block']}>
                <h3 className={styles.title}>{title}</h3>
                <div className={styles.date}>
                  <div>{dateLabelPrimary}</div>
                  <span> | </span>
                  <div>{dateLabelSecondary}</div>
                </div>
                {locationLabel && <div className={styles.date}>{locationLabel}</div>}
              </div>
              <div className={styles['price-block']}>
                {seatsLabel ? (
                  <div className={cn(styles['seats-label'], styles['seats-label-shows'])}>{seatsLabel}</div>
                ) : (
                  <div />
                )}
                <div className={styles['price-label']}>
                  <span className={styles.price}>{totalPriceFormatted}</span>
                  <span className={styles.currency}>{currencySymbol}</span>
                  <span className={styles['arrow-end']} />
                </div>
              </div>
            </div>
          </div>
          <img
            className={styles.image}
            src={_imageUrls[0]?.imageUrl}
            alt={_imageUrls[0]?.alt}
            onError={handleImageError}
          />
        </a>
      )}
      <div className={styles['line-block']}>
        <div className={styles.line} />
      </div>
    </li>
  )
}

const MAX_TICKETS_TO_SHOW_SEATS_LABEL = 10

const T = {
  soldOut: 'אזל המלאי',
}

const defineAvailableLabelPerShows = (resourceLevel: number) => {
  return `${resourceLevel || ''}${resourceLevel === 1 ? ' אחרון' : ' אחרונים'}`
}
